import React, { useEffect, useState } from 'react'
import { EventFilterCondition, EventFilterConditionOperatorType, EventFilterConditionType, EventFilterType } from '../../../models/EventFilter'
import { Controller, useForm } from 'react-hook-form'
import { EventFilterData, useBatchUpdateEventFiltersMutation, useGetAllEventFiltersQuery } from '../../../store/eventFilter/eventFilter'
import { EventFilterRow } from './EventFilterRow'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { Alert, Col, Container, Row, Spinner, Stack } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { addToast } from '../../../store/toast/toastSlice'
import classNames from 'classnames'
import { hasIncompleteConditions, isConditionComplete, isConditionStarted } from './eventFilterConditionValidation'

export type EventFilterConditionEdit = {
  id: string | null
  type: EventFilterConditionType | null
  operator: EventFilterConditionOperatorType | null
  value: string[] | null
  enforced: boolean
}

export type EventFilterEdit = {
  id?: string
  conditions: EventFilterConditionEdit[]
}

export type EventFilterBatch = {
  create?: EventFilterData[]
  update?: { [filterId: string]: EventFilterData }
  delete?: string[]
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type EventFiltersProps = {}

type FormValues = {
  autoJoinFilter: EventFilterEdit | null
  importOnlyFilter: EventFilterEdit | null
}

const defaultValues = {
  autoJoinFilter: null,
  importOnlyFilter: null,
}

export const EventFilters: React.FC<EventFiltersProps> = () => {
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { setValue, handleSubmit, control, getValues, watch } = useForm<FormValues>({ defaultValues })
  // eslint-disable-next-line react-hooks/incompatible-library
  const watchAutoJoinFilter = watch('autoJoinFilter')
  const watchImportJoinFilter = watch('importOnlyFilter')

  const { data: eventFilters, isLoading: isLoadingEventFiltersData } = useGetAllEventFiltersQuery({ accountId: accountId })
  const [persistedAutoJoinFilter, setPersistedAutoJoinFilter] = useState<EventFilterEdit | null>(null)
  const [persistedImportOnlyFilter, setPersistedImportOnlyFilter] = useState<EventFilterEdit | null>(null)
  const [lastAutoJoinFilter, setLastAutoJoinFilter] = useState<EventFilterEdit | null>({ conditions: [] })
  const [autoJoinFilterChanged, setAutoJoinFilterChanged] = useState<boolean>(false)
  const [importOnlyFilterChanged, setImportOnlyFilterChanged] = useState<boolean>(false)
  const [batchUpdateEventFilters, { isLoading: isProcessing }] = useBatchUpdateEventFiltersMutation()
  const { tracked } = useTracking()
  const dispatch = useAppDispatch()

  const loadIsHosted = (conditions: EventFilterCondition[]): EventFilterCondition[] =>
    conditions.map((c) =>
      c.type === 'is_hosted'
        ? {
            ...c,
            type: 'host',
            operator: c.operator === 'equals' ? 'me' : 'not_me',
            value: [],
          }
        : c,
    )

  const serializeIsHosted = (conditions: EventFilterConditionEdit[]) =>
    conditions.map((c) => ({
      type: ['me', 'not_me'].includes(c.operator!) ? 'is_hosted' : c.type!,
      operator: ['me', 'not_me'].includes(c.operator!) ? (c.operator === 'me' ? 'equals' : 'not_equals') : c.operator!,
      value: ['me', 'not_me'].includes(c.operator!) ? ['True'] : (c.value ?? []),
    }))

  useEffect(() => {
    if (eventFilters && eventFilters.data) {
      const firstAutoJoinFilter = eventFilters.data.find((filter) => filter.type === 'auto_join')
      const firstImportOnlyFilter = eventFilters.data.find((filter) => filter.type === 'import_only')
      const autoJoinFilter = firstAutoJoinFilter
        ? {
            id: firstAutoJoinFilter.id,
            conditions: loadIsHosted(firstAutoJoinFilter.conditions),
          }
        : null
      const importOnlyFilter = firstImportOnlyFilter
        ? {
            id: firstImportOnlyFilter.id,
            conditions: loadIsHosted(firstImportOnlyFilter.conditions),
          }
        : null
      setValue('autoJoinFilter', autoJoinFilter)
      setValue('importOnlyFilter', importOnlyFilter)
      setPersistedAutoJoinFilter(autoJoinFilter)
      setLastAutoJoinFilter(autoJoinFilter)
      setPersistedImportOnlyFilter(importOnlyFilter)
    }
  }, [eventFilters, setValue])

  const resetFilters = () => {
    setValue('autoJoinFilter', persistedAutoJoinFilter)
    setValue('importOnlyFilter', persistedImportOnlyFilter)
    setAutoJoinFilterChanged(false)
    setImportOnlyFilterChanged(false)
  }

  const saveFilter = (filterType: EventFilterType, filter: EventFilterEdit | null, batch: EventFilterBatch) => {
    const dirty = filterType === 'auto_join' ? autoJoinFilterChanged : importOnlyFilterChanged
    if (!dirty) {
      return
    }
    const persistedFilter = filterType === 'auto_join' ? persistedAutoJoinFilter : persistedImportOnlyFilter
    if (persistedFilter && (!filter || !filter.id)) {
      if (!batch.delete) {
        batch.delete = []
      }
      batch.delete.push(persistedFilter.id!)
    }
    const name = filterType === 'auto_join' ? 'Custom auto-join' : 'Custom import-only'
    if (filter) {
      const validConditions = serializeIsHosted(filter.conditions.filter((c) => !c.enforced && isConditionComplete(c)))
      if (filter.id) {
        if (!batch.update) {
          batch.update = {}
        }
        batch.update[filter.id] = { type: filterType, name, conditions: validConditions }
      } else {
        if (!batch.create) {
          batch.create = []
        }
        batch.create.push({ type: filterType, name, conditions: validConditions })
      }
    }
  }

  const pruneEmptyConditions = (filter: EventFilterEdit | null): EventFilterEdit | null =>
    filter ? { ...filter, conditions: filter.conditions.filter(isConditionStarted) } : null

  const _onFilterSubmit = async (data: FormValues) => {
    const eventFilterBatch = {} as EventFilterBatch
    saveFilter('auto_join', data.autoJoinFilter, eventFilterBatch)
    saveFilter('import_only', data.importOnlyFilter, eventFilterBatch)
    if (eventFilterBatch.create || eventFilterBatch.update || eventFilterBatch.delete) {
      try {
        await batchUpdateEventFilters({ accountId, batch: eventFilterBatch }).unwrap()
        dispatch(
          addToast({
            variant: 'success',
            title: 'Event Filters Saved',
            messages: ['Event Filters were successfully saved'],
          }),
        )
      } catch (e) {
        console.error(e)
      }
    }
    setValue('autoJoinFilter', pruneEmptyConditions(data.autoJoinFilter))
    setValue('importOnlyFilter', pruneEmptyConditions(data.importOnlyFilter))
    setAutoJoinFilterChanged(false)
    setImportOnlyFilterChanged(false)
  }

  const handleFilterSubmit = tracked(
    _onFilterSubmit,
    {
      eventName: TrackEvent.EVENT_FILTER_CHANGED,
      eventDetails: {
        userId: currentUserId,
      },
    },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return {
          autoJoinFilteredBy: data.autoJoinFilter?.conditions.map((c) => c.type).join(', '),
          importOnlyFilteredBy: data.importOnlyFilter?.conditions.map((c) => c.type).join(', '),
        }
      },
    },
  )

  const noFilters = !watchAutoJoinFilter && !watchImportJoinFilter
  const hasIncomplete = hasIncompleteConditions([watchAutoJoinFilter, watchImportJoinFilter])

  return (
    <Container className='d-flex flex-column gap-2 p-0'>
      {isLoadingEventFiltersData ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <h4>Event Filters</h4>
            </Col>
          </Row>
          <Container
            className={classNames('d-flex flex-column gap-2 p-3 zocks-border-0.5', {
              'zocks-border-primary': autoJoinFilterChanged || importOnlyFilterChanged,
              'zocks-border-gray-200': !autoJoinFilterChanged && !importOnlyFilterChanged,
            })}
          >
            <Row>
              <Col>
                <Alert variant='warning' className='d-flex gap-3 align-items-center'>
                  <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                  <span className='zocks-text-gray-900'>
                    The Zocks AI Assistant will only join calendar events automatically that meet all the filter conditions. If a filter condition has multiple
                    options, any event matching one of those options will be joined. If you would like to learn more, you can read our full{' '}
                    <a
                      target='_blank'
                      href='https://help.zocks.io/en/articles/10706694-how-do-i-configure-the-ai-assistant-to-automatically-join-or-sync-calendar-events'
                      rel='noreferrer'
                      className='button-link-medium zocks-text-gray-900'
                    >
                      Help&nbsp;article.
                    </a>
                  </span>
                </Alert>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col>
                <Controller
                  name='autoJoinFilter'
                  control={control}
                  render={({ field }) => (
                    <ScopedBlock
                      scope={{
                        scope: 'room_session:start',
                        superScope: ['room_session:admin', 'room:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <EventFilterRow
                        filterId='autoJoinFilter'
                        title='Filter for Auto Join'
                        description={
                          noFilters ? (
                            <span className='zocks-text-danger'>Auto Joining all events as no filtering is applied.</span>
                          ) : field.value && !field.value.conditions.length ? (
                            'Auto Joining all events as no filter conditions are set.'
                          ) : !field.value ? (
                            'Auto Join is off. Enable to allow the AI Assistant to join events.'
                          ) : (
                            ''
                          )
                        }
                        value={field.value}
                        defaultValue={() => lastAutoJoinFilter}
                        save={(data) => {
                          setAutoJoinFilterChanged(true)
                          setValue('autoJoinFilter', data)
                          if (data != null) {
                            setLastAutoJoinFilter(data)
                          }
                        }}
                      />
                    </ScopedBlock>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Alert variant='warning' className='d-flex gap-3 align-items-center'>
                  <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                  <span className='zocks-text-gray-900'>
                    You can add a second filter category to select which calendar events to display and which ones to automatically send your AI assistant to
                    join. 'Only Display on Upcoming' events will show up in the Upcoming menu but require you to manually send the AI notetaker to join. <br />
                    If you would like to learn more, you can read our full{' '}
                    <a
                      target='_blank'
                      href='https://help.zocks.io/en/articles/10706694-how-do-i-configure-the-ai-assistant-to-automatically-join-or-sync-calendar-events'
                      rel='noreferrer'
                      className='button-link-medium zocks-text-gray-900'
                    >
                      Help&nbsp;article.
                    </a>
                  </span>
                </Alert>
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name='importOnlyFilter'
                  control={control}
                  render={({ field }) => (
                    <EventFilterRow
                      filterId='importOnlyFilter'
                      title='Only Display on Upcoming'
                      description={
                        noFilters
                          ? ''
                          : !field.value
                            ? 'Only Display on Upcoming is off. Enable to sync meetings to Upcoming.'
                            : !field.value.conditions.length
                              ? 'Syncing all events to Upcoming as no filter conditions are set.'
                              : ''
                      }
                      value={field.value}
                      defaultValue={() => ({
                        conditions:
                          getValues('autoJoinFilter')?.conditions.map((c) => ({
                            ...c,
                            id: null,
                          })) ?? [],
                      })}
                      save={(data) => {
                        setImportOnlyFilterChanged(true)
                        setValue('importOnlyFilter', data)
                      }}
                    />
                  )}
                />
              </Col>
            </Row>
            {(autoJoinFilterChanged || importOnlyFilterChanged) && (
              <Row className='d-flex gap-3 justify-content-end mt-5'>
                <Col xs='auto'>
                  <Stack direction='horizontal' className='gap-3'>
                    {hasIncomplete ? (
                      <span className='paragraph-small zocks-text-danger-200 align-self-center'>Complete all conditions before saving.</span>
                    ) : null}
                    <Button variant='secondary' onClick={() => resetFilters()}>
                      Cancel
                    </Button>
                    <ScopedBlock
                      scope={{
                        scope: 'room:list',
                        superScope: ['room:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Button
                        variant='primary'
                        type='submit'
                        onClick={handleSubmit(handleFilterSubmit)}
                        loading={isProcessing}
                        disabled={(!autoJoinFilterChanged && !importOnlyFilterChanged) || hasIncomplete}
                      >
                        Save
                      </Button>
                    </ScopedBlock>
                  </Stack>
                </Col>
              </Row>
            )}
          </Container>
        </>
      )}
    </Container>
  )
}
